import { useIsCurrentUserHunter } from "Hooks/useIsCurrentUserHunter";
import { useIsCurrentUserMerchant } from "Hooks/useIsCurrentUserMerchant";
import { useLoggedInUser } from "Hooks/useLoggedInUser";

export type ProfileRequirementKey = "region";

export type ProfileRequirement = {
  key: ProfileRequirementKey;
};

/**
 * Ordered list of the logged-in user's unmet profile requirements — the single place that
 * decides what the ProfileCompletionModal asks for. All requirements block the app; future
 * "add information before continuing" prompts append here.
 * The handle deliberately has NO requirement: it's collected by a skippable onboarding step,
 * and users who skipped see the dashboard HandleMissingBanner nudge (hard enforcement
 * eventually lives at the chat/social feature gates).
 */
export const useProfileRequirements = (): Array<ProfileRequirement> => {
  const user = useLoggedInUser();

  // Business roles (Merchant/Hunter/HunterPlus) collect their region inline in the onboarding form
  // (first step = business information: region → IČO → register lookup) or inherit it from the license,
  // so the region requirement must never fire for them. It's the region-selection surface for customers —
  // who have no business form — and the general backstop for the region invariant.
  const isHunter = useIsCurrentUserHunter();
  const isMerchant = useIsCurrentUserMerchant();
  const setsRegionViaForm = isHunter || isMerchant;

  if (!user) {
    return [];
  }

  // biome-ignore lint/style/useConst: using let to hint intent of mutating the array contents
  let requirements: Array<ProfileRequirement> = [];

  if (user.regionRequired && !setsRegionViaForm) {
    requirements.push({ key: "region" });
  }

  return requirements;
};
